import React, { useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { useStatsigClient } from "@statsig/react-bindings";

import { ECrossSitePromotionType } from "../../@types";
import { AllstarSiteData, PartnerViewSession } from "../../State";
import { PromoSession } from "../../State/promoSession";
import { FirstLandTakeover } from "./Components/FirstLandTakeover";
import { SideRailAd } from "./Components/SideRailAd";
import { CrossSitePromotionViewModel } from "./ViewModel";
import {
  checkForPromotionExperiments,
  shouldShowExperimentPromotion,
} from "MVVM/Utilities/filterPromotions";

type CrossSitePromotionProps = {
  type?: ECrossSitePromotionType;
  doneLoadingCallback?: (promotionsData: any) => boolean;
  loadDataOnly?: boolean;
};

export const CrossSitePromotion = ({
  type,
  loadDataOnly,
}: CrossSitePromotionProps) => {
  const [partnerViewSession, setPartnerViewSession] =
    useRecoilState(PartnerViewSession);
  const { promotionsData, promotionsLoading }: any =
    CrossSitePromotionViewModel();
  const [siteData, setSiteData] = useRecoilState(AllstarSiteData);
  const [loadedSiteData, setLoadedSiteData] = useState(false);
  const [promoSession, setPromoSession] = useRecoilState(PromoSession);
  const { getExperiment } = useStatsigClient();
  useEffect(() => {
    if (promotionsData && loadDataOnly && !promotionsLoading) {
      const filteredPromotions = checkForPromotionExperiments(
        promotionsData.promotions,
        (experimentName) =>
          shouldShowExperimentPromotion(experimentName, getExperiment),
        getExperiment,
      );

      setPromoSession({
        ...promoSession,
        ...filteredPromotions,
        loaded: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionsLoading]);

  if (promotionsLoading) return <></>;

  switch (type) {
    case ECrossSitePromotionType.FirstLandTakeover: {
      const filteredFirstLandModal = checkForPromotionExperiments(
        promotionsData?.promotions,
        (experimentName) =>
          shouldShowExperimentPromotion(experimentName, getExperiment),
        getExperiment,
      )?.activeSiteFirstLandModal;
      if (filteredFirstLandModal?.length)
        return <FirstLandTakeover overlaySlides={filteredFirstLandModal} />;
      else return <></>;
    }
    case ECrossSitePromotionType.SideRailAd: {
      const filteredSideRailAd = checkForPromotionExperiments(
        promotionsData?.promotions,
        (experimentName) =>
          shouldShowExperimentPromotion(experimentName, getExperiment),
        getExperiment,
      )?.activeSiteSideRailAd;
      if (filteredSideRailAd) return <SideRailAd {...filteredSideRailAd} />;
      else return <></>;
    }

    case ECrossSitePromotionType.VideoPlayerCTA: {
      const filteredVideoPlayerPromos = checkForPromotionExperiments(
        promotionsData?.promotions,
        (experimentName) =>
          shouldShowExperimentPromotion(experimentName, getExperiment),
        getExperiment,
      )?.activeSiteVideoPlayerPromos;
      if (
        partnerViewSession?.overlayConfig?.display &&
        filteredVideoPlayerPromos?.length &&
        !partnerViewSession.promotion.display
      ) {
        setPartnerViewSession({
          ...partnerViewSession,
          promotion: {
            ...filteredVideoPlayerPromos[0],
            display: true,
          },
        });
      }
      return <></>; // we dont need to return content here, but we need the viewmodel to gather data and IF it is a partnerView session, set the data. For the overlay
    }
    case ECrossSitePromotionType.CarouselSlide: {
      const filteredCarouselPromos = checkForPromotionExperiments(
        promotionsData?.promotions,
        (experimentName) =>
          shouldShowExperimentPromotion(experimentName, getExperiment),
        getExperiment,
      )?.activeCarouselPromos;
      if (filteredCarouselPromos?.length) {
        if (loadDataOnly && !loadedSiteData) {
          setLoadedSiteData(true);

          const promosToDisplay = [];

          for (const promotion of filteredCarouselPromos) {
            promosToDisplay.push({
              ...promotion,
              video: promotion.image,
              image: promotion.image,
              buttonCopy: promotion.buttonCopy,
            });
          }
          setSiteData({
            ...siteData,
            promotions: promosToDisplay,
          });
          return <></>;
        } else {
          return <></>;
        }
      } else {
        if (!loadedSiteData && !filteredCarouselPromos) {
          setLoadedSiteData(true);
          setSiteData({
            ...siteData,
            promotions: undefined,
          });
        }

        return <></>;
      }
    }

    default:
      return <></>;
  }
};
